import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { AccountFacade } from 'ish-core/facades/account.facade';
import { ShoppingFacade } from 'ish-core/facades/shopping.facade';
import { AccountNavigationBlock } from 'ish-core/models/account-navigation-block/account-navigation-block.model';
import { NavigationCategory } from 'ish-core/models/navigation-category/navigation-category.model';
import { PRESIDIO_HOME_CONTENT } from 'ish-shared/dias/home/presidio-home-content';
import { PresidioImpersonationFacade } from 'src/app/extensions/presidio-demo/services/presidio-impersonation.facade';
import { PresidioWarehouseCustomerService } from 'src/app/extensions/presidio-demo/services/presidio-warehouse-customer.service';

import {
  NavigationItem,
} from '../../../pages/account/account-navigation/account-navigation.component';
import { accountNavigationIcon } from '../../../pages/account/account-navigation/account-navigation.icons';
/** Theme-swapped base path — webpack resolves `.presidio` / `.all` / base. */
import { b2bNavigationItems } from '../../../pages/account/account-navigation/account-navigation.items';

import { PresidioShellUiFacade } from './presidio-shell-ui.facade';

const FAVORITES_ID = 'my-favorite';
const OVERVIEW_ID = 'my-account';

/**
 * Presidio "All" catalog drawer — Amazon-style:
 * scrollable main menu (shop + account); subcategory selection replaces the view
 * with a Main Menu back control (fixed drawer width, no side expansion).
 * Dialog a11y: Escape (back then close), focus move/restore, Tab trap, aria-modal + inert.
 */
@Component({
  selector: 'custom-all-drawer',
  templateUrl: './custom-all-drawer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CustomAllDrawerComponent implements OnInit, OnDestroy {
  @ViewChild('prsAllDrawerPanel') panelRef?: ElementRef<HTMLElement>;
  @ViewChild('prsAllDrawerClose') closeBtnRef?: ElementRef<HTMLButtonElement>;

  open$: Observable<boolean>;
  categories$: Observable<NavigationCategory[]>;
  isLoggedIn$: Observable<boolean>;
  /** MVP 8.2.2 — Co-Browse control for CSR sessions only. */
  canImpersonate$: Observable<boolean>;
  /** MVP 2.6.3 — My Warehouse portal link (warehouse accounts only). */
  isWarehouseCustomer$: Observable<boolean>;
  /** Portal Shop destination — same catalog root as desktop portal nav / home CTA. */
  readonly catalogRootCategoryId = PRESIDIO_HOME_CONTENT.hero.primaryCategoryId || 'SHOP';

  /**
   * My Account sections for the drawer footer — same source as account sidebar.
   * Favourites appear only when the user has pinned items or custom blocks.
   */
  accountNavItems$: Observable<NavigationItem[]>;
  favoriteBlocks$: Observable<AccountNavigationBlock[]>;

  iconFor(item: Pick<NavigationItem, 'id' | 'icon'> | { id: string; icon?: string }): string {
    return accountNavigationIcon(item.id, item.icon);
  }

  /** MVP 2.6.3 — My Warehouse first-nav category (catalog WH). */
  private isMyWarehouseCategory(category: NavigationCategory | undefined | null): boolean {
    const id = (category?.uniqueId || '').trim().toLowerCase();
    return id === 'wh' || id === 'my-warehouse';
  }

  private readonly destroy$ = new Subject<void>();
  private readonly selectedCategorySubject = new BehaviorSubject<NavigationCategory | null>(null);
  private isOpen = false;
  private previouslyFocused: HTMLElement | null = null;

  readonly selectedCategory$ = this.selectedCategorySubject.asObservable();
  readonly hasSubpanel$ = this.selectedCategory$.pipe(map(category => !!category));
  readonly subCategories$ = this.selectedCategorySubject.pipe(
    switchMap(category =>
      category?.hasChildren
        ? this.shoppingFacade.navigationCategories$(category.uniqueId).pipe(
            map(categories => categories.filter(item => !item?.hideInMenu))
          )
        : of([] as NavigationCategory[])
    )
  );

  constructor(
    private shellUi: PresidioShellUiFacade,
    private shoppingFacade: ShoppingFacade,
    private accountFacade: AccountFacade,
    private impersonationFacade: PresidioImpersonationFacade,
    private warehouseCustomer: PresidioWarehouseCustomerService
  ) {}

  ngOnInit(): void {
    this.open$ = this.shellUi.allDrawerOpen$;
    this.isLoggedIn$ = this.accountFacade.isLoggedIn$;
    this.canImpersonate$ = this.impersonationFacade.canImpersonate$;
    this.isWarehouseCustomer$ = this.warehouseCustomer.isWarehouseCustomer$;
    // MVP 2.6.3 — My Warehouse only for warehouse accounts (trees can merge across logins).
    this.categories$ = combineLatest([
      this.shoppingFacade.navigationCategories$(),
      this.isWarehouseCustomer$.pipe(startWith(false)),
    ]).pipe(
      map(([categories, isWarehouseCustomer]) =>
        categories.filter(
          category =>
            !category?.hideInMenu && (isWarehouseCustomer || !this.isMyWarehouseCategory(category))
        )
      )
    );

    this.favoriteBlocks$ = combineLatest([
      this.accountFacade.accountNavigationFavoriteIds$,
      this.accountFacade.accountNavigationBlocks$,
    ]).pipe(map(([favoriteIds, blocks]) => blocks.filter(block => favoriteIds.includes(block.id))));

    this.accountNavItems$ = combineLatest([
      this.accountFacade.accountNavigationFavoriteIds$,
      this.favoriteBlocks$,
    ]).pipe(map(([favoriteIds, favoriteBlocks]) => this.buildAccountNavItems(favoriteIds, favoriteBlocks)));

    this.open$.pipe(takeUntil(this.destroy$)).subscribe(open => {
      const wasOpen = this.isOpen;
      this.isOpen = open;
      if (open && !wasOpen) {
        this.focusPanel();
      } else if (!open && wasOpen) {
        this.clearSelection();
        this.restoreFocus();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  focusCategory(category: NavigationCategory): void {
    if (category.hasChildren) {
      this.selectedCategorySubject.next(category);
    }
  }

  clearSelection(): void {
    this.selectedCategorySubject.next(null);
  }

  close(): void {
    this.clearSelection();
    this.shellUi.closeAllDrawer();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.isOpen) {
      return;
    }
    // Amazon pattern: Escape backs out of subcategory before closing the drawer.
    if (this.selectedCategorySubject.value) {
      this.clearSelection();
      return;
    }
    this.close();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.isOpen || event.key !== 'Tab' || SSR) {
      return;
    }
    const panel = this.panelRef?.nativeElement;
    if (!panel) {
      return;
    }
    const focusable = this.getFocusable(panel);
    if (!focusable.length) {
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    } else if (active && !panel.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  }

  /** Exposed for unit tests — builds the account footer list from favorite state. */
  buildAccountNavItems(favoriteIds: string[], favoriteBlocks: AccountNavigationBlock[] = []): NavigationItem[] {
    const items: NavigationItem[] = [];
    const overview = b2bNavigationItems.find(item => item.id === OVERVIEW_ID);
    if (overview) {
      items.push(overview);
    }

    const favoriteNavItems = this.resolveFavoriteNavItems(favoriteIds);
    if (favoriteNavItems.length || favoriteBlocks.length) {
      items.push({
        id: FAVORITES_ID,
        localizationKey: 'account.my_favorite.heading',
        icon: 'bi-star-fill',
        children: favoriteNavItems,
      });
    }

    for (const item of b2bNavigationItems) {
      if (item.id === OVERVIEW_ID || item.id === FAVORITES_ID) {
        continue;
      }
      if (item.children?.length || item.routerLink) {
        items.push(item);
      }
    }
    return items;
  }

  private resolveFavoriteNavItems(favoriteIds: string[]): NavigationItem[] {
    if (!favoriteIds?.length) {
      return [];
    }
    const favoriteSet = new Set(favoriteIds);
    return b2bNavigationItems
      .filter(item => item.id !== FAVORITES_ID)
      .flatMap(item => item.children ?? [item])
      .filter(item => favoriteSet.has(item.id) && !!item.routerLink);
  }

  private focusPanel(): void {
    if (SSR) {
      return;
    }
    this.previouslyFocused = (document.activeElement as HTMLElement) ?? null;
    queueMicrotask(() => {
      (this.closeBtnRef?.nativeElement ?? this.panelRef?.nativeElement)?.focus?.();
    });
  }

  private restoreFocus(): void {
    if (SSR) {
      return;
    }
    const target = this.previouslyFocused;
    this.previouslyFocused = null;
    queueMicrotask(() => target?.focus?.());
  }

  private getFocusable(root: HTMLElement): HTMLElement[] {
    return Array.from(
      root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(el => !el.hasAttribute('disabled') && el.tabIndex !== -1 && el.offsetParent !== null);
  }
}
